import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Shield, Phone, MessageCircle, Sun, CarFront, AlertTriangle, AlertCircle, Send, ChevronLeft, Check, Clock, Lock, Zap, ArrowRight, Mic, MicOff, PhoneOff, Baby, PawPrint, Package, PersonStanding, MapPin, Ban } from "lucide-react";
import useAnonymousCall from "../hooks/useAnonymousCall";
import CallHealthPanel from "../components/CallHealthPanel";
import { CallRequestChip, isCallRequestMsg } from "../components/call/CallRequestChip";
import "./PublicQRv2.css";
// CORTE 3-A — i18n del escáner: idioma por request + tipo de protección (SOLO TEXTO)
import { T, setScannerLocale, browserLangFallback, scannerType } from "../i18n/publicScanner";

const API = '/api';

// ========== BLOQUE 3-B — ICONOS TIPADOS POR protection_type (solo capa visual) ==========
const TYPE_ICON = {
  vehicle: (p) => <CarFront {...p} />,
  kid: (p) => <Baby {...p} />,
  pet: (p) => <PawPrint {...p} />,
  object: (p) => <Package {...p} />,
  other: (p) => <Shield {...p} />,
};
const QA_ICONS = {
  luces:   { vehicle: (p) => <Sun {...p} />,      kid: (p) => <PersonStanding {...p} />, pet: (p) => <PawPrint {...p} />, object: (p) => <Package {...p} />, other: (p) => <AlertCircle {...p} /> },
  bloqueo: { vehicle: (p) => <CarFront {...p} />, kid: (p) => <MapPin {...p} />,         pet: (p) => <MapPin {...p} />,   object: (p) => <Ban {...p} />,     other: (p) => <AlertCircle {...p} /> },
};
const TypeIcon = ({ size = 20 }) => (TYPE_ICON[scannerType()] || TYPE_ICON.vehicle)({ size });
const QAIcon = ({ action, size = 22 }) => {
  const map = QA_ICONS[action];
  if (!map) return <AlertTriangle size={size} />;
  return (map[scannerType()] || map.vehicle)({ size });
};

// ========== ACTION OUTCOMES STATE MACHINE (PUSH-ONLY) ==========
// AvisoCar uses ZERO paid channels. Notification can only succeed via PWA push.
//   idle                       → before click
//   enviando_notificacion      → request in flight
//   notificado_push            → backend pushed via PWA (FREE)
//   mensaje_guardado_sin_push  → message saved, push had no active device.
//                                Important: we DO NOT block messaging — the
//                                owner will see it the next time they open
//                                AvisoCar. This is the explicit user demand:
//                                "MESSAGE MUST ALWAYS WORK".
//   sin_canal                  → identical wire status to mensaje_guardado_sin_push
//                                but used by the QUICK-ACTION buttons that have
//                                no chat fallback (Luces / Bloqueo / Riesgo).
//   fallo                      → genuine delivery failure (server 5xx etc.)
const mapOutcome = (status, channel) => {
  if (status === "sent" && channel === "push") return "notificado_push";
  if (status === "skipped") return "sin_canal";
  if (status === "failed") return "fallo";
  if (status === "sent") return "notificado_push";
  return "fallo";
};

// iter58 (fase 2.3): honest copy per state. UI never lies, never alarms the
// scanner when their message was safely persisted. A null / undefined status
// (backend not reporting) is treated as "saved" not "failed" — the message
// row is always inserted before any push attempt (see anonymous_comms.py).
const mapChatOutcome = (status, channel) => {
  if (status === "sent" && channel === "push") return "notificado_push";
  if (status === "sent") return "notificado_push";
  if (status === "skipped") return "mensaje_guardado_sin_push";
  if (status === "failed") return "fallo_real";     // push attempted, gateway rejected
  return "mensaje_guardado_sin_push";               // null/unknown → default to saved-honest copy
};

const OUTCOME_COPY = {
  enviando_notificacion:     { get text() { return T("out_sending"); }, cls: "sending" },
  notificado_push:           { get text() { return T("out_sent"); }, cls: "sent-whatsapp" },
  mensaje_guardado_sin_push: { get text() { return T("out_saved"); }, cls: "saved" },
  sin_canal:                 { get text() { return T("out_nochannel"); }, cls: "no-channel" },
  fallo_real:                { get text() { return T("out_fail"); }, cls: "warn" },
  // Legacy alias — some places (call flow) still emit 'fallo'. Route it to the honest saved copy.
  fallo:                     { get text() { return T("out_fail"); }, cls: "warn" },
};

const ActionStateBanner = ({ action, state }) => {
  if (!state) return null;
  const copy = OUTCOME_COPY[state];
  if (!copy) return null;
  return (
    <div
      className={`action-state-banner ${copy.cls}`}
      data-testid={`action-state-${action}-${state}`}
      data-testid-state={state}
    >
      <span className="asb-dot" />
      <span className="asb-label">{action}</span>
      <span className="asb-text">{copy.text}</span>
    </div>
  );
};

// ========== TRUST BADGES ==========
const TrustBadges = () => (
  <div className="trust-badges">
    {[
      { icon: <Lock size={16} />, text: T("badge_anon") },
      { icon: <Phone size={16} />, text: T("badge_number") },
      { icon: <Shield size={16} />, text: T("badge_secure") },
      { icon: <Zap size={16} />, text: T("badge_fast") },
    ].map((b, i) => (
      <div key={i} className="trust-badge">
        <span className="trust-icon">{b.icon}</span>
        <span>{b.text}</span>
      </div>
    ))}
  </div>
);

// ========== QUICK ACTION CARD ==========
const QuickActionCard = ({ icon, label, color, onClick, disabled, tid }) => (
  <button
    className={`quick-action-card ${color}`}
    onClick={onClick}
    disabled={disabled}
    data-testid={`quick-action-${tid || label.toLowerCase().replace(/\s/g, '-')}`}
  >
    <div className="qa-icon">{icon}</div>
    <span className="qa-label">{label}</span>
    <ArrowRight size={16} className="qa-arrow" />
  </button>
);

// ========== EMERGENCY MODAL ==========
const EmergencyModal = ({ token, onClose, onSuccess }) => {
  const [step, setStep] = useState("warning"); // warning -> options -> sent
  const [loading, setLoading] = useState(false);
  const [emergencyOptions, setEmergencyOptions] = useState(null);
  const [outcome, setOutcome] = useState(null); // { status, channel }

  // iter64 — emergency-contacts feature purged; the emergency button always fires a direct push
  // notification to the owner. No 'options' step needed anymore.
  const handleConfirm = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/public/qr/${token}/emergency`);
      setOutcome({
        status: r.data?.notification_status || null,
        channel: r.data?.notification_channel || null,
      });
      onSuccess?.({ status: r.data?.notification_status, channel: r.data?.notification_channel });
      setStep("sent");
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerEmergency = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/public/qr/${token}/emergency`);
      setOutcome({
        status: r.data?.notification_status || null,
        channel: r.data?.notification_channel || null,
      });
      onSuccess?.({ status: r.data?.notification_status, channel: r.data?.notification_channel });
      setStep("sent");
    } catch (err) {
      console.error(err);
      setOutcome({ status: "failed", channel: null });
      onSuccess?.({ status: "failed", channel: null });
      setStep("sent");
    } finally {
      setLoading(false);
    }
  };

  if (step === "warning") {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="emergency-modal" onClick={e => e.stopPropagation()}>
          <div className="emergency-header">
            <AlertCircle size={32} />
            <h3>{T("emg_title")}</h3>
          </div>
          <p className="emergency-text">
            {T("emg_text")}{" "}{T("emg_alert")}
          </p>
          <div className="emergency-privacy">
            <Lock size={14} />
            <span>{T("emg_privacy")}</span>
          </div>
          <div className="emergency-actions">
            <button className="btn-emergency" onClick={handleConfirm} disabled={loading} data-testid="confirm-emergency-btn">
              {loading ? T("emg_loading") : T("emg_confirm")}
            </button>
            <button className="btn-cancel" onClick={onClose}>{T("emg_cancel")}</button>
          </div>
        </div>
      </div>
    );
  }

  // Options screen — depends on owner's live config
  if (step === "options" && emergencyOptions) {
    const mode = emergencyOptions.mode || "first_available";
    const labels = emergencyOptions.anonymous_labels || [];
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="emergency-modal" onClick={e => e.stopPropagation()} data-testid="emergency-options-modal">
          <div className="emergency-header">
            <AlertCircle size={28} />
            <h3>{T("emg_contacts_title")}</h3>
          </div>
          <div className="emergency-privacy">
            <Lock size={14} />
            <span>{T("emg_labels_note")}</span>
          </div>

          {mode === "first_available" ? (
            <div className="emergency-actions" style={{ marginTop: 14 }}>
              {emergencyOptions.allow_emergency_calls && (
                <button className="btn-emergency" onClick={handleTriggerEmergency} disabled={loading} data-testid="emergency-call-first">
                  📞 {T("emg_call_first")}
                </button>
              )}
              {emergencyOptions.allow_urgent_messages && (
                <button className="btn-primary" onClick={handleTriggerEmergency} disabled={loading} data-testid="emergency-msg-first">
                  💬 {T("emg_send_urgent")}
                </button>
              )}
              <button className="btn-cancel" onClick={onClose}>{T("emg_cancel")}</button>
            </div>
          ) : (
            <div className="emergency-actions" style={{ marginTop: 14 }}>
              {labels.map((label, i) => (
                <button
                  key={i}
                  className="btn-primary"
                  onClick={handleTriggerEmergency}
                  disabled={loading}
                  data-testid={`emergency-contact-${i + 1}`}
                  style={{ textAlign: "left" }}
                >
                  🛡️ {label}
                </button>
              ))}
              <button className="btn-cancel" onClick={onClose}>{T("emg_cancel")}</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Sent / disabled state
  const outcomeState = outcome ? mapOutcome(outcome.status, outcome.channel) : null;
  const outcomeCopy = outcomeState ? OUTCOME_COPY[outcomeState] : null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="emergency-modal sent" onClick={e => e.stopPropagation()}>
        <div className="emergency-header sent">
          <AlertCircle size={32} />
          <h3>{T("emg_sent_title")}</h3>
        </div>
        <p className="emergency-text">
          {T("emg_sent_body")}
        </p>
        {outcomeCopy && (
          <div
            className={`action-state-banner ${outcomeCopy.cls}`}
            data-testid={`emergency-outcome-${outcomeState}`}
            data-testid-state={outcomeState}
          >
            <span className="asb-dot" />
            <span className="asb-text">{outcomeCopy.text}</span>
          </div>
        )}
        <div className="emergency-privacy">
          <Shield size={14} />
          <span>{T("emg_anon")}</span>
        </div>
        <button className="btn-primary" onClick={() => { onClose(); }}>{T("emg_ok")}</button>
      </div>
    </div>
  );
};

// ========== EMOJI QUICK PICKER ==========
const QUICK_EMOJIS = ["👍", "🙏", "🚗", "🆘", "⚠️", "🔥", "💡", "🚨", "✅", "❤️", "😊", "🙋"];

const EmojiPicker = ({ onPick }) => (
  <div className="chat-emoji-bar" data-testid="chat-emoji-bar">
    {QUICK_EMOJIS.map((e) => (
      <button key={e} className="chat-emoji-btn" onClick={() => onPick(e)} data-testid={`chat-emoji-${e}`}>
        {e}
      </button>
    ))}
  </div>
);

// ========== CHAT PANEL ==========
const DEBUG_OVERLAY = (() => {
  try { return new URLSearchParams(window.location.search).get("debug") === "1"; }
  catch { return false; }
})();

const DebugOverlay = ({ data }) => {
  if (!DEBUG_OVERLAY) return null;
  return (
    <pre
      data-testid="scanner-debug-overlay"
      style={{
        background: "#0b1320", color: "#f1f5ff", fontSize: 10,
        padding: 8, borderRadius: 8, margin: "8px 0", whiteSpace: "pre-wrap",
        wordBreak: "break-all", fontFamily: "monospace",
      }}
    >
      {JSON.stringify(data, null, 2)}
    </pre>
  );
};

const ChatPanel = ({ token, sessionId, setSessionId, vehicleName, onCall }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [resumed, setResumed] = useState(false);
  // notifyState machine: idle | enviando_notificacion | notificado_push | sin_canal | fallo
  const [notifyState, setNotifyState] = useState(null);
  const [ownerLinkOpenedAt, setOwnerLinkOpenedAt] = useState(null);
  const [debugInfo, setDebugInfo] = useState({});
  const [showEmoji, setShowEmoji] = useState(false);
  const messagesEndRef = useRef(null);

  // Load messages on session change + every 5s polling
  useEffect(() => {
    // Reset all per-session state when sessionId flips (prevents cross-session
    // contamination, e.g. an "owner opened" flag from a previous session
    // bleeding into a fresh chat).
    setOwnerLinkOpenedAt(null);
    setDebugInfo({});
    setMessages([]);
    if (sessionId) {
      // Show "Continuando conversacion segura" if this session was loaded from storage
      try {
        const stored = localStorage.getItem(`avisocar_session_${token}`);
        if (stored === sessionId) setResumed(true);
      } catch (_) { /* ignore */ }
      loadMessages(true);
      const interval = setInterval(() => loadMessages(false), 5000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // iter71 4.3 — memoria de bloqueo best-effort: un navegador bloqueado
  // arranca bloqueado al re-escanear. Límite honesto: el anonimato impide un
  // bloqueo perfecto (otro navegador/dispositivo no comparte esta memoria).
  useEffect(() => {
    try {
      if (localStorage.getItem(`avisocar_blocked_${token}`) === "1") {
        setNotifyState("session_blocked");
      }
    } catch (_) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => {
    if (notifyState === "session_blocked") {
      try { localStorage.setItem(`avisocar_blocked_${token}`, "1"); } catch (_) { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifyState]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadMessages = async (initial = false) => {
    if (!initial) setRefreshing(true);
    try {
      const sidAtRequest = sessionId;
      const res = await axios.get(`${API}/public/chat/${sidAtRequest}`);
      // Guard against late responses arriving after sessionId has changed.
      if (res.data?.session_id && res.data.session_id !== sidAtRequest) {
        return;
      }
      setMessages(res.data.messages || []);
      // STRICT TRUTH: only the explicit boolean from the backend (which is
      // computed only from `owner_magic_opened_at`, never from the dashboard
      // `owner_read_at` flag) flips the "Propietario abrio enlace" banner.
      if (res.data.owner_opened_boolean === true && res.data.owner_link_opened_at) {
        setOwnerLinkOpenedAt(res.data.owner_link_opened_at);
      } else {
        setOwnerLinkOpenedAt(null);
      }
      // iter59 fase 3: surface terminal lifecycle states from the poll.
      const lc = res.data.lifecycle_status;
      if (lc === "blocked") setNotifyState("session_blocked");
      else if (lc === "expired") setNotifyState("session_expired");
      setDebugInfo({
        session_id: res.data.session_id,
        push_sent: res.data.push_sent,
        push_failed_reason: res.data.push_failed_reason,
        owner_link_opened_at: res.data.owner_link_opened_at,
        owner_opened_boolean: res.data.owner_opened_boolean,
        call_status: res.data.call_status,
      });
      // If owner replied, clear active notify state (owner is here, conversation is live)
      if ((res.data.messages || []).some(m => m.sender === "owner")) {
        setNotifyState(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (!initial) setTimeout(() => setRefreshing(false), 350);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    // iter59 fase 3: block send when session already reached a terminal end-state
    if (notifyState === "session_expired" || notifyState === "session_blocked" || notifyState === "qr_rate_limited" || notifyState === "message_rate_limited") return;
    setSending(true);
    setNotifyState("enviando_notificacion");
    try {
      let notif_status = null;
      let notif_channel = null;
      if (!sessionId) {
        const res = await axios.post(`${API}/public/qr/${token}/chat`, { message: input.trim() });
        setSessionId(res.data.session_id);
        try { localStorage.setItem(`avisocar_session_${token}`, res.data.session_id); } catch (_) { /* ignore */ }
        setMessages([{
          id: res.data.message_id,
          sender: "scanner",
          sender_label: "Tu",
          message: input.trim(),
          status: "delivered",
          created_at: new Date().toISOString(),
        }]);
        notif_status = res.data.notification_status;
        notif_channel = res.data.notification_channel;
      } else {
        const res = await axios.post(`${API}/public/chat/${sessionId}/message`, { message: input.trim() });
        notif_status = res.data?.notification_status || null;
        notif_channel = res.data?.notification_channel || null;
        await loadMessages(false);
      }
      setInput("");
      setShowEmoji(false);
      setNotifyState(mapChatOutcome(notif_status, notif_channel));
    } catch (err) {
      // iter59 fase 3: map terminal server errors to honest UI states.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const code = (detail && typeof detail === "object" ? detail.code : null) || detail;
      if (status === 429 && code === "message_rate_limited") {
        setNotifyState("message_rate_limited");
      } else if (status === 429 && (code === "qr_rate_limited" || code === undefined)) {
        setNotifyState("qr_rate_limited");
      } else if (status === 410 && code === "session_expired") {
        setNotifyState("session_expired");
      } else if (status === 403 && code === "session_blocked") {
        setNotifyState("session_blocked");
      } else {
        console.error(err);
        setNotifyState("fallo_real");
      }
    } finally {
      setSending(false);
    }
  };

  const handleEmojiPick = (emoji) => {
    setInput((prev) => (prev + emoji).slice(0, 500));
  };

  return (
    <div className="chat-panel">
      <div className="chat-header">
        <MessageCircle size={18} />
        <span>{T("chat_header")}</span>
        <Lock size={14} className="chat-lock" />
        {onCall && (
          <button
            type="button"
            className="chat-header-call-btn"
            onClick={onCall}
            data-testid="chat-header-call-btn"
            aria-label={T("chat_call_aria")}
          >
            <Phone size={14} /> {T("chat_call_btn")}
          </button>
        )}
      </div>

      {/* Resume banner */}
      {resumed && messages.length > 0 && (
        <div className="chat-resume-banner" data-testid="chat-resume-banner">
          <Shield size={12} /> {T("chat_resume")}
        </div>
      )}

      {/* Status indicators row — real runtime state machine (no fake "enviado") */}
      {(refreshing || notifyState || ownerLinkOpenedAt) && (
        <div className="chat-status-row">
          {refreshing && <span className="chat-status refreshing" data-testid="chat-status-refreshing">{T("chat_refreshing")}</span>}
          {notifyState === "enviando_notificacion" && (
            <span className="chat-status notifying" data-testid="chat-status-enviando">{T("chat_notifying")}</span>
          )}
          {notifyState === "notificado_push" && !ownerLinkOpenedAt && (
            <span className="chat-status waiting" data-testid="chat-status-notificado-push">{T("chat_waiting")}</span>
          )}
          {notifyState === "mensaje_guardado_sin_push" && !ownerLinkOpenedAt && (
            <span className="chat-status ok" data-testid="chat-status-saved-no-push">
              {T("out_saved")}
            </span>
          )}
          {notifyState === "sin_canal" && (
            <span className="chat-status warn" data-testid="chat-status-sin-canal">{T("chat_nochannel")}</span>
          )}
          {(notifyState === "fallo" || notifyState === "fallo_real") && (
            <span className="chat-status warn" data-testid="chat-status-fallo">{T("chat_fail")}</span>
          )}
          {notifyState === "session_expired" && (
            <span className="chat-status warn" data-testid="chat-status-session-expired">{T("chat_expired")}</span>
          )}
          {notifyState === "session_blocked" && (
            <span className="chat-status warn" data-testid="chat-status-session-blocked">{T("chat_blocked")}</span>
          )}
          {notifyState === "qr_rate_limited" && (
            <span className="chat-status warn" data-testid="chat-status-qr-rate-limited">{T("chat_ratelimited")}</span>
          )}
          {notifyState === "message_rate_limited" && (
            <span className="chat-status warn" data-testid="chat-status-message-rate-limited">{T("chat_msg_ratelimited")}</span>
          )}
          {ownerLinkOpenedAt && (
            <span className="chat-status ok" data-testid="chat-status-owner-opened">{T("chat_owner_opened")}</span>
          )}
        </div>
      )}
      <DebugOverlay data={debugInfo} />

      <div className="chat-messages">
        {messages.length === 0 && !sessionId && (
          <div className="chat-empty">
            <Shield size={24} />
            <p>{T("chat_empty")}</p>
            <span>{T("chat_empty_sub")}</span>
          </div>
        )}
        {/* iter71 2.3 — N intentos de llamada = UN chip, nunca N burbujas */}
        <CallRequestChip msgs={messages.filter(isCallRequestMsg)} />
        {messages.filter(m => !isCallRequestMsg(m)).map(msg => (
          <div key={msg.id} className={`chat-bubble ${msg.sender === "scanner" ? "sent" : "received"}`}>
            <div className="bubble-label">{msg.sender === "scanner" ? T("bubble_you") : T("owner_short")}</div>
            <div className="bubble-text">{msg.message}</div>
            <div className="bubble-meta">
              <Clock size={10} />
              <span>{new Date(msg.created_at).toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" })}</span>
              {msg.sender === "scanner" && msg.status === "delivered" && <Check size={10} />}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {showEmoji && <EmojiPicker onPick={handleEmojiPick} />}

      <div className="chat-input-bar">
        <button
          type="button"
          className="btn-emoji"
          onClick={() => setShowEmoji((s) => !s)}
          data-testid="chat-emoji-toggle"
          aria-label="emoji"
        >
          😊
        </button>
        <input
          type="text"
          placeholder={T("chat_placeholder")}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleSend()}
          maxLength={500}
          data-testid="chat-input"
        />
        <button onClick={handleSend} disabled={!input.trim() || sending} className="btn-send" data-testid="chat-send-btn">
          <Send size={18} />
        </button>
      </div>
    </div>
  );
};

// ========== CALL PANEL — Default anonymous in-app call request (NO Twilio, NO phone) ==========
// CALL CONTRACT (per user spec):
//   - NO PUSH = NO CALL ATTEMPT. If push_sent=false from /call-request, we
//     skip waiting entirely and immediately show "No se pudo iniciar la
//     llamada. Puedes enviar un mensaje." with an auto-switch to chat.
//   - If push_sent=true, we wait MAX 45s for owner to OPEN the magic link.
//     If they don't, render "Sin respuesta. Puedes dejar un mensaje."
//   - If owner opened but didn't ACCEPT within 30s, render
//     "El propietario abrio, pero no acepto la llamada."
//   - If accepted but WebRTC fails → emit [CALL_FAILED] (already done by
//     useAnonymousCall) and render "La llamada no conecto. Continua por chat."
const NO_OPEN_TIMEOUT_MS = 45_000;
const NO_ACCEPT_TIMEOUT_MS = 30_000;
const CallPanel = ({ token, onBack, setChatSessionId, switchToChat }) => {
  const [phase, setPhase] = useState("creating"); // creating | waiting | push_unavailable | accepted | declined | replied | error | ended | no_answer | opened_no_accept
  const [callReason, setCallReason] = useState(null); // no_push_subscription | push_failed | owner_not_available
  const [sessionId, setSessionId] = useState(null);
  // iter60: truth anchor — every banner/timer compares against THIS call's timestamp
  const [callRequestedAt, setCallRequestedAt] = useState(null);
  // real notification outcome state machine (same semantics as chat)
  const [notifyState, setNotifyState] = useState(null);

  // WebRTC hook (scanner role). Auto-starts ONLY when owner accepts.
  const { phase: callPhase, muted, durationSec, error: callError, start, toggleMute, hangup, health } = useAnonymousCall({
    sessionId,
    role: "scanner",
    autoStart: false,
  });

  const createdRef = useRef(false);

  // Kick off: create call request immediately, no phone asked.
  // NOTE: React 18 StrictMode double-invokes effects in dev/preview.
  // We rely ONLY on `createdRef.current` to guard against duplicate POSTs.
  // Do NOT add a `cancelled` flag here — if the effect cleanup sets it to
  // true, the awaited axios response would be discarded and the UI would
  // be stuck at phase="creating". Seen in iter42/iter43 regression tests.
  useEffect(() => {
    if (createdRef.current) return;
    createdRef.current = true;
    // Explicit marker for field debugging to confirm NEW flow is running in prod
    console.log("NEW CALL FLOW ACTIVE");
    // iter60 ROOT-CAUSE FIX: previously, an existing chat session in storage
    // was silently reused with phase="waiting" and NO push was ever fired —
    // the owner never knew about the call ("Contactando..." lied). Now we
    // ALWAYS POST a call-request: on the existing session if we have one
    // (server dedupes pending calls), or a fresh one via the QR endpoint.
    const fireCallRequest = async (existingSid) => {
      setNotifyState("enviando_notificacion");
      const url = existingSid
        ? `${API}/public/chat/${existingSid}/call-request`
        : `${API}/public/qr/${token}/call-request`;
      const res = await axios.post(url);
      const sid = res.data.session_id;
      setSessionId(sid);
      try { localStorage.setItem(`avisocar_session_${token}`, sid); } catch (_) {}
      setChatSessionId(sid);
      setCallRequestedAt(res.data.call_requested_at || new Date().toISOString());
      const outcome = mapOutcome(res.data.notification_status, res.data.notification_channel);
      setNotifyState(outcome);
      setCallReason(res.data.reason || null);  // no_push_subscription | push_failed | owner_not_available
      // ABSOLUTE RULE: NO PUSH = NO CALL ATTEMPT.
      // If the backend confirmed push didn't go out, do NOT spin in
      // "Contactando al propietario...". Drop straight to fallback.
      const pushSucceeded = res.data.notification_status === "sent";
      setPhase(pushSucceeded ? "waiting" : "push_unavailable");
    };
    (async () => {
      let existing = null;
      try { existing = localStorage.getItem(`avisocar_session_${token}`); } catch (_) {}
      try {
        await fireCallRequest(existing);
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const code = (detail && typeof detail === "object" ? detail.code : null) || detail;
        // P2/P3 §6 — incoming call ceiling: honest, neutral state (no retry loop).
        if (status === 429) {
          setNotifyState("call_rate_limited");
          setCallReason("call_rate_limited");
          setPhase("call_rate_limited");
          return;
        }
        // Stale/blocked/expired stored session → drop it and start fresh.
        if (existing && (status === 404 || status === 410 || status === 403)) {
          try { localStorage.removeItem(`avisocar_session_${token}`); } catch (_) {}
          try {
            await fireCallRequest(null);
            return;
          } catch (_) { /* fall through to error state */ }
        }
        void code;
        setNotifyState("fallo");
        setCallReason("push_failed");
        setPhase("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll the session to detect owner link-open → accept → reply
  const [ownerLinkOpenedAt, setOwnerLinkOpenedAt] = useState(null);
  const [debugInfo, setDebugInfo] = useState({});
  useEffect(() => {
    if (!sessionId) return;
    // Reset per-session truth on session change
    setOwnerLinkOpenedAt(null);
    setDebugInfo({});
    let interval = null;
    const poll = async () => {
      try {
        const sidAtRequest = sessionId;
        const res = await axios.get(`${API}/public/chat/${sidAtRequest}`);
        const data = res.data || {};
        // Drop late responses for an earlier session
        if (data.session_id && data.session_id !== sidAtRequest) return;
        // Reload-resume branch: if push didn't go out, never wait — fall back.
        if (phase === "waiting" && data.push_sent === false && data.push_failed_reason) {
          setPhase("push_unavailable");
          setCallReason(
            data.push_failed_reason === "no_active_device" || data.push_failed_reason === "no_subscription"
              ? "no_push_subscription"
              : "push_failed"
          );
          return;
        }
        // STRICT TRUTH (iter60): the "abrio enlace" banner only counts opens
        // that happened AFTER this call was requested. A magic-link open from
        // an earlier chat exchange is NOT a reaction to the call.
        const anchor = callRequestedAt || data.call_requested_at || null;
        if (!callRequestedAt && data.call_requested_at) {
          setCallRequestedAt(data.call_requested_at);
        }
        if (
          data.owner_opened_boolean === true &&
          data.owner_link_opened_at &&
          anchor &&
          Date.parse(data.owner_link_opened_at) > Date.parse(anchor)
        ) {
          setOwnerLinkOpenedAt(data.owner_link_opened_at);
        } else {
          setOwnerLinkOpenedAt(null);
        }
        setDebugInfo({
          session_id: data.session_id,
          push_sent: data.push_sent,
          push_failed_reason: data.push_failed_reason,
          owner_link_opened_at: data.owner_link_opened_at,
          owner_opened_boolean: data.owner_opened_boolean,
          call_status: data.call_status,
          call_requested_at: data.call_requested_at,
        });
        const status = data.call_request_status;
        // iter71 — GHOST-CALL FIX (evidencia Corte 0: intento #2 arrancó 400ms
        // después del hangup sin nueva solicitud). El poll SOLO puede armar la
        // llamada desde "waiting"; jamás desde ended/failed/error (estados
        // terminales locales del escáner).
        if (status === "accepted" && phase === "waiting") {
          setPhase("accepted");
          // Start WebRTC as soon as owner accepts
          start();
        } else if (status === "declined" && phase === "waiting") setPhase("declined");
        else if (status === "replied" && phase === "waiting") setPhase("replied");
      } catch (_) { /* ignore */ }
    };
    interval = setInterval(poll, 3000);
    poll();
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, phase]);

  // Stage 1 — owner did NOT open the magic link in NO_OPEN_TIMEOUT_MS.
  // iter60: anchored to call_requested_at (NOT to previous session state) so
  // a resumed panel counts down from the real call time.
  useEffect(() => {
    if (phase !== "waiting" || ownerLinkOpenedAt) return;
    const elapsed = callRequestedAt ? Date.now() - Date.parse(callRequestedAt) : 0;
    const remaining = Math.max(0, NO_OPEN_TIMEOUT_MS - (Number.isFinite(elapsed) ? elapsed : 0));
    const t = setTimeout(() => {
      setPhase((cur) => (cur === "waiting" ? "no_answer" : cur));
    }, remaining);
    return () => clearTimeout(t);
  }, [phase, ownerLinkOpenedAt, callRequestedAt]);

  // Stage 2 — owner OPENED the link (after call_requested_at) but did not
  // accept within NO_ACCEPT_TIMEOUT_MS. Anchored to the post-call open time.
  useEffect(() => {
    if (phase !== "waiting" || !ownerLinkOpenedAt) return;
    const elapsed = Date.now() - Date.parse(ownerLinkOpenedAt);
    const remaining = Math.max(0, NO_ACCEPT_TIMEOUT_MS - (Number.isFinite(elapsed) ? elapsed : 0));
    const t = setTimeout(() => {
      setPhase((cur) => (cur === "waiting" ? "opened_no_accept" : cur));
    }, remaining);
    return () => clearTimeout(t);
  }, [phase, ownerLinkOpenedAt]);

  const fmt = (s) => `${Math.floor(s/60).toString().padStart(2,"0")}:${(s%60).toString().padStart(2,"0")}`;

  // If WebRTC connection is live, override UI with in-call controls
  if (phase === "accepted" && (callPhase === "connecting" || callPhase === "connected")) {
    return (
      <div className="call-anon-panel" data-testid="call-anon-panel">
        <div className="call-status-block accepted">
          <div className={`call-ringing active ${callPhase === "connected" ? "" : "pulsing"}`}>
            <Phone size={34} />
            {callPhase === "connecting" && <span className="ring-pulse" />}
          </div>
          <h3 data-testid={callPhase === "connected" ? "call-connected" : "call-connecting"}>
            {callPhase === "connected" ? T("call_connected") : T("call_connecting2")}
          </h3>
          <p>{callPhase === "connected" ? T("call_speak") : T("call_establishing")}</p>
          {callPhase === "connected" && (
            <div className="call-timer" data-testid="call-timer">{fmt(durationSec)}</div>
          )}
          <div className="call-privacy"><Lock size={14} /><span>{T("call_encrypted")}</span></div>

          <div className="call-controls">
            <button onClick={toggleMute} className={`call-ctrl ${muted ? "muted" : ""}`} data-testid="call-mute-btn">
              {muted ? <MicOff size={20} /> : <Mic size={20} />}
              <span>{muted ? T("call_muted") : T("call_mute")}</span>
            </button>
            <button onClick={() => { hangup(); setPhase("ended"); }} className="call-ctrl end" data-testid="call-end-btn">
              <PhoneOff size={20} />
              <span>{T("call_hang")}</span>
            </button>
          </div>
          <CallHealthPanel health={health} role="scanner" />
        </div>
      </div>
    );
  }

  // If WebRTC failed after owner accepted → fallback UI
  if (phase === "accepted" && callPhase === "failed") {
    return (
      <div className="call-anon-panel" data-testid="call-anon-panel">
        <div className="call-status-block declined">
          <Phone size={32} className="call-icon-error" />
          <h3 data-testid="call-failed">{T("call_failed_title")}</h3>
          <p>{callError === "mic_denied"
            ? T("call_mic_denied")
            : callError === "connect_timeout"
            ? T("call_timeout_body")
            : T("call_chat_works")}</p>
          <button className="btn-primary" data-testid="call-failed-to-chat-btn" onClick={() => (switchToChat ? switchToChat() : onBack())}>{T("call_continue_chat")}</button>
          <button className="btn-secondary" onClick={start}>{T("call_retry")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="call-anon-panel" data-testid="call-anon-panel">
      {phase === "creating" && (
        <div className="call-status-block">
          <div className="call-ringing">
            <Phone size={32} />
          </div>
          <h3>{T("call_preparing_title")}</h3>
          <p>{T("call_preparing")}</p>
          {/* 3-B: acceso al chat siempre visible, también mientras se crea la solicitud */}
          <button className="btn-secondary" onClick={() => (switchToChat ? switchToChat() : onBack())} data-testid="call-creating-to-chat-btn">
            {T("call_switch_msg")}
          </button>
        </div>
      )}

      {phase === "waiting" && (
        <div className="call-status-block">
          <div className="call-ringing active">
            <Phone size={34} />
            <span className="ring-pulse" />
          </div>
          <h3>{T("call_contacting")}</h3>
          {/* Real runtime notification state */}
          <div className="call-state-stack">
            {notifyState === "enviando_notificacion" && (
              <div className="call-step" data-testid="call-state-enviando">{T("call_sending")}</div>
            )}
            {notifyState === "notificado_push" && (
              <div className="call-step" data-testid="call-state-notificado-push">{T("call_notified")}</div>
            )}
            {notifyState === "sin_canal" && callReason === "no_push_subscription" && (
              <div className="call-step warn" data-testid="call-state-sin-canal">
                {T("call_no_push_active")}
              </div>
            )}
            {notifyState === "sin_canal" && callReason === "owner_not_available" && (
              <div className="call-step warn" data-testid="call-state-owner-not-available">
                {T("call_alerts_off")}
              </div>
            )}
            {notifyState === "sin_canal" && !callReason && (
              <div className="call-step warn" data-testid="call-state-sin-canal-generic">{T("call_not_available")}</div>
            )}
            {notifyState === "fallo" && (
              <div className="call-step warn" data-testid="call-state-fallo">
                {callReason === "push_failed" ? T("call_fail_deliver") : T("call_no_notify")}
              </div>
            )}
            {/* Owner lifecycle — no identity exposed */}
            {ownerLinkOpenedAt ? (
              <div className="call-step ok" data-testid="call-state-owner-opened">
                {T("call_owner_opened")}
              </div>
            ) : (
              <div className="call-step">{T("call_waiting_resp")}</div>
            )}
          </div>
          <DebugOverlay data={debugInfo} />
          <div className="call-privacy" style={{ marginTop: 10 }}>
            <Lock size={14} />
            <span>{T("call_number_hidden")}</span>
          </div>
          <p className="call-hint">{T("call_hint")}</p>
          <button className="btn-secondary" onClick={onBack} data-testid="call-switch-to-chat-btn">{T("call_switch_msg")}</button>
        </div>
      )}

      {/* NO PUSH = NO CALL — never spin in 'Contactando...' if push didn't go out. */}
      {phase === "push_unavailable" && (
        <div className="call-status-block declined">
          <Phone size={32} className="call-icon-error" />
          <h3 data-testid="call-push-unavailable">{T("call_push_unavail_title")}</h3>
          <p>
            {callReason === "no_push_subscription"
              ? T("call_pu_no_sub")
              : callReason === "owner_not_available"
              ? T("call_pu_alerts_off")
              : T("call_pu_generic")}
          </p>
          <button
            className="btn-primary"
            data-testid="call-fallback-to-chat-btn"
            onClick={() => (switchToChat ? switchToChat() : onBack())}
          >
            {T("call_send_msg")}
          </button>
        </div>
      )}

      {/* P2/P3 §6 — call ceiling reached: neutral copy, chat stays available. */}
      {phase === "call_rate_limited" && (
        <div className="call-status-block declined">
          <Phone size={32} className="call-icon-error" />
          <h3 data-testid="call-rate-limited">{T("chat_call_ratelimited")}</h3>
          <button
            className="btn-primary"
            data-testid="call-rate-limited-to-chat-btn"
            onClick={() => (switchToChat ? switchToChat() : onBack())}
          >
            {T("call_send_msg")}
          </button>
        </div>
      )}

      {phase === "opened_no_accept" && (
        <div className="call-status-block declined">
          <Phone size={32} className="call-icon-error" />
          <h3 data-testid="call-opened-no-accept">{T("call_opened_no_accept")}</h3>
          <p>{T("call_ona_body")}</p>
          <button
            className="btn-primary"
            data-testid="call-leave-message-btn"
            onClick={() => (switchToChat ? switchToChat() : onBack())}
          >
            {T("call_leave_msg")}
          </button>
        </div>
      )}

      {phase === "no_answer" && (
        <div className="call-status-block declined">
          <Phone size={32} className="call-icon-error" />
          <h3 data-testid="call-no-answer">{T("call_no_answer")}</h3>
          <p>
            {callReason === "no_push_subscription"
              ? T("call_na_no_sub")
              : callReason === "owner_not_available"
              ? T("call_na_alerts_off")
              : callReason === "push_failed"
              ? T("call_na_push_failed")
              : T("call_na_generic")}
          </p>
          <button
            className="btn-primary"
            data-testid="call-leave-message-btn"
            onClick={() => (switchToChat ? switchToChat() : onBack())}
          >
            {T("call_leave_msg")}
          </button>
        </div>
      )}

      {phase === "accepted" && callPhase === "idle" && (
        <div className="call-status-block accepted">
          <Phone size={32} className="call-icon-active" />
          <h3>{T("call_accepted")}</h3>
          <p>{T("call_connecting_audio")}</p>
          <button className="btn-secondary" onClick={() => (switchToChat ? switchToChat() : onBack())} data-testid="call-accepted-to-chat-btn">
            {T("call_switch_msg")}
          </button>
        </div>
      )}

      {phase === "replied" && (
        <div className="call-status-block accepted">
          <MessageCircle size={32} />
          <h3>{T("call_replied")}</h3>
          <p>{T("call_replied_body")}</p>
          <button className="btn-primary" onClick={onBack} data-testid="call-open-chat-btn">{T("call_open_chat")}</button>
        </div>
      )}

      {phase === "declined" && (
        <div className="call-status-block declined">
          <Phone size={32} className="call-icon-error" />
          <h3>{T("call_declined_title")}</h3>
          <p>{T("call_declined_body")}</p>
          <button className="btn-primary" onClick={onBack}>{T("call_write_msg")}</button>
        </div>
      )}

      {(phase === "error" || phase === "ended") && (
        <div className="call-status-block declined">
          <Phone size={32} className="call-icon-error" />
          <h3>{phase === "ended" ? T("call_ended") : T("call_error_title")}</h3>
          <p>{T("call_continue_msg")}</p>
          <button className="btn-secondary" onClick={onBack}>{T("call_back")}</button>
        </div>
      )}
    </div>
  );
};

// ========== MAIN PAGE ==========
const PublicQR = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [vehicleData, setVehicleData] = useState(null);
  const [error, setError] = useState(null);
  const [view, setView] = useState("main"); // main, chat, call
  const [chatSessionId, setChatSessionId] = useState(() => {
    try { return localStorage.getItem(`avisocar_session_${window.location.pathname.split("/").pop()}`) || null; } catch { return null; }
  });
  const [showEmergency, setShowEmergency] = useState(false);
  // Per-action state machine: { luces: 'idle|enviando_notificacion|notificado_whatsapp|notificado_sms|sin_canal|fallo', ... }
  const [actionState, setActionState] = useState({});
  // iter69-B A3c — SOLO si el dueño está logueado en ESTE navegador y el QR es SUYO.
  // Escáneres anónimos jamás disparan la llamada (no hay token → no request).
  const [ownerWarn, setOwnerWarn] = useState(false);

  useEffect(() => {
    let hasToken = false;
    try { hasToken = !!localStorage.getItem("avisocar_token"); } catch (_) { hasToken = false; }
    if (!hasToken) return;
    axios.get(`${API}/user/qr-owner-status/${token}`)
      .then((r) => setOwnerWarn(!!(r.data?.is_mine && r.data?.avisos_inactivos)))
      .catch(() => setOwnerWarn(false));
  }, [token]);

  useEffect(() => {
    setScannerLocale(browserLangFallback(), "other"); // fallback pre-fetch (loading/error)
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/public/qr/${token}`);
      if (res.data.is_active) {
        setScannerLocale(res.data.scanner_lang || browserLangFallback(),
                         res.data.protection_type || "vehicle");
        setVehicleData(res.data);
      } else {
        setError("inactive");
      }
    } catch (err) {
      setError("not_found");
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = async (actionType) => {
    setActionState(prev => ({ ...prev, [actionType]: "enviando_notificacion" }));
    try {
      const res = await axios.post(`${API}/public/qr/${token}/quick-action`, { action_type: actionType });
      const next = mapOutcome(res.data.notification_status, res.data.notification_channel);
      setActionState(prev => ({ ...prev, [actionType]: next }));
    } catch (err) {
      console.error(err);
      setActionState(prev => ({ ...prev, [actionType]: "fallo" }));
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="pqr-container">
        <div className="pqr-loading">
          <div className="pqr-spinner" />
          <p>{T("loading")}</p>
        </div>
      </div>
    );
  }

  // Error states
  if (error) {
    return (
      <div className="pqr-container">
        <div className="pqr-error-card">
          <Shield size={48} />
          <h2>{error === "inactive" ? T("err_inactive_title") : T("err_notfound_title")}</h2>
          <p>{error === "inactive"
            ? T("err_inactive_body")
            : T("err_notfound_body")
          }</p>
        </div>
      </div>
    );
  }

  // Chat view
  if (view === "chat") {
    return (
      <div className="pqr-container">
        <button className="pqr-back" onClick={() => setView("main")} data-testid="back-to-main">
          <ChevronLeft size={20} /> {T("call_back")}
        </button>
        <ChatPanel
          token={token}
          sessionId={chatSessionId}
          setSessionId={setChatSessionId}
          vehicleName={vehicleData?.vehicle_name}
          onCall={() => setView("call")}
        />
      </div>
    );
  }

  // Call view
  if (view === "call") {
    return (
      <div className="pqr-container">
        <button className="pqr-back" onClick={() => setView("main")} data-testid="back-to-main">
          <ChevronLeft size={20} /> {T("call_back")}
        </button>
        <CallPanel
          token={token}
          onBack={() => setView("chat")}
          setChatSessionId={setChatSessionId}
          switchToChat={() => setView("chat")}
        />
      </div>
    );
  }

  // Main view
  return (
    <div className="pqr-container" data-testid="qr-scanner-landing">
      {showEmergency && <EmergencyModal
        token={token}
        onClose={() => setShowEmergency(false)}
        onSuccess={(res) => {
          const next = mapOutcome(res?.status, res?.channel);
          setActionState(prev => ({ ...prev, emergencia: next }));
        }}
      />}

      {/* Header */}
      <div className="pqr-header">
        <div className="pqr-logo" data-testid="scanner-brand">
          {/* PRIORITY 5 — the official AvisoCar lockup, not a generic category
              icon. The protection type is already stated in the header title. */}
          <img
            src="/brand/avisocar-logo-trim.png"
            srcSet="/brand/avisocar-logo-128.png 128w, /brand/avisocar-logo-256.png 256w"
            sizes="128px"
            alt="AvisoCar"
            className="pqr-logo-img"
            data-testid="scanner-brand-logo"
          />
        </div>
        <h1>{T("header_title")}</h1>
        <p>{T("header_sub")}</p>
      </div>

      <TrustBadges />

      {/* iter69-B A3c — visible SOLO para el dueño logueado de ESTE vehículo */}
      {ownerWarn && (
        <div
          style={{
            margin: "0 0 12px", padding: "10px 14px", borderRadius: 12,
            background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.45)",
            color: "#fecaca", fontSize: 13, fontWeight: 700, textAlign: "center",
          }}
          data-testid="own-qr-revival-warning"
        >
          ⚠️ Tus avisos están inactivos — entra a AvisoCar
        </div>
      )}

      {/* Vehicle info */}
      <div className="pqr-vehicle-card" data-testid="qr-vehicle-card">
        <TypeIcon size={20} />
        <div>
          <strong>{vehicleData?.display_name || T("card_noun")}</strong>
          {vehicleData?.plate_number && <span className="pqr-plate">{vehicleData.plate_number}</span>}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="pqr-section">
        <h2 className="pqr-section-title">{T("qa_title")}</h2>
        <div className="quick-actions-grid">
          <QuickActionCard
            icon={<QAIcon action="luces" />}
            label={T("qa_luces")}
            color="yellow"
            tid="luces"
            onClick={() => handleQuickAction("luces")}
            disabled={actionState.luces === "enviando_notificacion"}
          />
          <QuickActionCard
            icon={<QAIcon action="bloqueo" />}
            label={T("qa_bloqueo")}
            color="blue"
            tid="bloqueo"
            onClick={() => handleQuickAction("bloqueo")}
            disabled={actionState.bloqueo === "enviando_notificacion"}
          />
          <QuickActionCard
            icon={<AlertTriangle size={22} />}
            label={T("qa_riesgo")}
            color="orange"
            tid="riesgo"
            onClick={() => handleQuickAction("riesgo")}
            disabled={actionState.riesgo === "enviando_notificacion"}
          />
          <QuickActionCard
            icon={<AlertCircle size={22} />}
            label={T("qa_emergencia")}
            color="red"
            tid="emergencia"
            onClick={() => setShowEmergency(true)}
            disabled={false}
          />
        </div>
        {/* Per-button real runtime state (no fake success). Shown inline only if an action has been fired. */}
        <div className="action-state-stack">
          <ActionStateBanner action={T("qa_luces")}    state={actionState.luces} />
          <ActionStateBanner action={T("qa_bloqueo")}   state={actionState.bloqueo} />
          <ActionStateBanner action={T("qa_riesgo")}  state={actionState.riesgo} />
          <ActionStateBanner action={T("qa_emergencia")}          state={actionState.emergencia} />
        </div>
      </div>

      {/* Communication Buttons */}
      <div className="pqr-section">
        <h2 className="pqr-section-title">{T("comm_title")}</h2>
        <button className="pqr-comm-btn chat" onClick={() => setView("chat")} data-testid="open-chat-btn">
          <MessageCircle size={22} />
          <div>
            <strong>{T("comm_chat_title")}</strong>
            <span>{T("comm_chat_sub")}</span>
          </div>
          <ArrowRight size={18} />
        </button>
        <button className="pqr-comm-btn call" onClick={() => setView("call")} data-testid="call-owner-btn">
          <Phone size={22} />
          <div>
            <strong>{T("comm_call_title")}</strong>
            <span>{T("comm_call_sub")}</span>
          </div>
          <ArrowRight size={18} />
        </button>
      </div>

      {/* Footer */}
      <div className="pqr-footer">
        <Lock size={12} />
        <span>{T("footer")}</span>
      </div>
    </div>
  );
};

export default PublicQR;
